import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { InputField } from "../common/InputField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";

export const Kontak = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState("");
  const [isSending, setIsSending] = useState(false);

  const fadeIn = (delay = 0) => ({
    initial: { opacity: 0, y: 15 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay, ease: "easeOut" },
    viewport: { once: true },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setStatus("Please fill in all fields.");
      return;
    }

    setIsSending(true);
    setStatus("Sending message...");

    const serviceId =
      import.meta.env.VITE_EMAILJS_SERVICE_ID || "service_z82jhct";
    const templateId =
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID || "template_9wvasfo";
    const publicKey =
      import.meta.env.VITE_EMAILJS_PUBLIC_KEY || "vnmhykBI-1OZax2wq";

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      message: formData.message,
      subject: "Contact Form Message",
    };

    try {
      const result = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      if (result && (result.text === "OK" || result.status === 200)) {
        setStatus("Message sent successfully!");
        setFormData({ name: "", email: "", message: "" });
      } else {
        console.error("EmailJS unexpected response:", result);
        setStatus("Something went wrong. Please try again later.");
      }
    } catch (err) {
      console.error("Error sending email via EmailJS:", err);
      setStatus("Failed to send message. Please try again later.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="w-full min-h-screen py-40 bg-white text-slate-700">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          {/* Contact Info */}
          <motion.div {...fadeIn(0.3)}>
            <motion.p
              {...fadeIn(0.2)}
              className="text-2xl sm:text-3xl md:text-5xl font-black mb-6"
            >
              Contact Us
            </motion.p>

            <p className="text-lg text-slate-700 leading-relaxed mb-10">
           If you have any
              questions about our digital solutions, need technical support, or would like to collaborate with us, please
              feel free to reach out using the contact details below:
            </p>

            <div className="space-y-4">
              <motion.div {...fadeIn(0.4)}>
                <div className="flex items-start">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="w-5 h-5 text-slate-700 mt-1 mr-4"
                  />
                  <div>
                    <p className="font-semibold text-slate-700">Email</p>
                    <a
                      href="mailto:contact@vionixasia.com"
                      className="text-lg text-slate-700 hover:underline"
                    >
                      contact@vionixasia.com
                    </a>
                  </div>
                </div>
              </motion.div>

              <motion.div {...fadeIn(0.5)}>
                <div className="flex items-start">
                  <FontAwesomeIcon
                    icon={faPhone}
                    className="w-5 h-5 text-slate-700 mt-1 mr-4"
                  />
                  <div>
                    <p className="font-semibold text-slate-700">Phone</p>
                    <a
                      href="https://wa.me/6281212303426"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg text-slate-700 hover:underline"
                    >
                      +62-812-1230-3426
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div {...fadeIn(0.4)}>
            <h3 className="text-2xl font-bold text-slate-700 mb-8">
              Send a Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <InputField
                label="Full Name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                labelColor="text-slate-700"
              />
              <InputField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                labelColor="text-slate-700"
              />
              <InputField
                label="Message"
                name="message"
                type="textarea"
                rows={8}
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Write your message here..."
                labelColor="text-slate-700"
              />

              <button
                type="submit"
                disabled={isSending}
                className={`w-full py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-semibold text-lg transition-colors ${
                  isSending ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {isSending ? "Sending…" : "Send Message"}
              </button>
            </form>

            {status && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-6 p-5 rounded-xl text-center font-medium ${
                  status.includes("successfully")
                    ? "bg-green-50 text-green-700"
                    : status.includes("Sending")
                    ? "bg-slate-50 text-slate-700"
                    : "bg-red-50 text-red-700"
                }`}
              >
                {status}
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Kontak;
